import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateCarbonCaches } from '@/lib/carbonCacheKeys';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FullscreenFormDialog } from '@/components/ui/fullscreen-form-dialog';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AccountSelect } from '@/components/accounting/ui/account-select';
import { useShowAccountCurrency } from '@/components/accounting/ui/useAccountLabelHelpers';
import { FundSelect } from '@/components/accounting/ui/fund-select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Plus, Trash2, HelpCircle, Lightbulb, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { CurrencyDisplay } from '@/components/ui/currency-display';
import {
  useCreateLedgerTransaction,
  useUpdateLedgerTransaction,
  useUpdateLedgerTransactionHeader,
  buildJournalCreateBody,
} from '@/hooks/useLedgerTransactions';
import { declaredSettlementIdOf } from '@/lib/vatDeclared';
import { DeclaredVatBanner } from '@/components/accounting/DeclaredVatBanner';
import { recurringPayloadSchemaByType, type RecurringTemplateWire } from '@releve/shared';
import { DangerZone } from '@/components/ui/danger-zone';
import { RecurringSection } from './recurring/RecurringSection';
import {
  resolveRecurringDefaults,
  validateRecurrence,
  type RecurringFormFields,
} from './recurring/recurringFormFields';
import { useRecurringTemplateSubmit } from './recurring/useRecurringTemplateSubmit';
import { buildRecurringPayload, hydrateJournalFromPayload } from '@/lib/recurringPayload';
import { todayInOrgZone, type RecurrenceFormSchemaContext } from '@/lib/recurrence';
import { useChartOfAccounts, useAccountById, buildAccountHierarchy } from '@/hooks/useChartOfAccounts';
import { HierarchicalAccount, ChartOfAccount } from '@/types/financial-accounts';
import { useFunds } from '@/hooks/useFunds';
import { useProjects } from '@/hooks/useProjects';
import { useBusinesses } from '@/hooks/useBusinesses.ts';
import { useForm, useFieldArray } from 'react-hook-form';
import { format } from 'date-fns';
import { parseLocalISODate } from '@/lib/dateUtils';
import { LineItemCarbon } from '@/components/carbon/LineItemCarbon';
import {
  type CarbonData,
  carbonDataToRow,
  persistTransactionCarbonResults,
} from '@/hooks/useCarbonTracking';
import { BudgetCategoryPicker } from '@/components/budgets/BudgetCategoryPicker';
import { WkrCategorySelect } from '@/components/tax/nl/wkr/WkrCategorySelect';
import { useNlWkrSourceCategories } from '@/hooks/useNlWkr';
import type { NlWkrCategory } from '@releve/shared';
import { useSaveBudgetCategoryEntries, useDeleteBudgetCategoryEntriesByLedgerEntries, useBudgetCategoryEntriesByTransaction } from '@/hooks/budgets/useBudgetCategoryEntries';
import { supabase } from '@/integrations/supabase/client';
import { useSuspenseAccount } from '@/hooks/useSuspenseAccount';
import { useOpeningBalanceEquityAccount } from '@/hooks/useOpeningBalanceEquityAccount';
import { useSuspenseAccountBalance } from '@/hooks/useSuspenseAccountBalance';
import { useTranslation } from '@/hooks/useTranslation';
import { captureException } from '@/lib/sentry';
import { toast } from '@/hooks/useToast';

/**
 * Per-line fund override sentinels — form state only, never sent to the RPC.
 *
 * `entries[i].fund_id` means:
 *   `''`                 → inherit the header fund (the default for new rows)
 *   `NO_FUND_SENTINEL`   → explicit "no fund", i.e. do NOT inherit the header
 *   `<uuid>`             → this line's own fund
 *
 * The explicit-no-fund case needs its own value because the mutation hooks
 * coerce `entry.fund_id || null`, so a plain `''` is indistinguishable from
 * "unset". `onSubmit` resolves both sentinels back to `''`/`<uuid>`.
 */
const NO_FUND_SENTINEL = '__no_fund__';
/** The override select's value for `''` (inherit). Mapped both ways at the control. */
const INHERIT_FUND_VALUE = 'inherit';
/** "No fund" option on the header/helper selects — mapped to `''`, as Project/Business do. */
const NO_FUND_OPTION = 'none';

interface TransactionEntry {
  /** ledger_entries.id — present on loaded entries (edit mode), absent on new rows. */
  id?: string;
  account_id: string;
  /** `''` = inherit the header fund; see NO_FUND_SENTINEL. */
  fund_id?: string;
  debit_amount?: number;
  credit_amount?: number;
  description?: string;
  /** NL WKR classification ('' = none); jurisdiction-gated in the UI. */
  wkr_category?: string;
  /**
   * The filed VAT return this line was declared on (loaded entries only,
   * never form state). One tagged line makes the whole journal *declared*:
   * lines and date freeze, the save becomes header-only (docs/TAX.md
   * "Edit guard").
   */
  vat_settlement_id?: string | null;
}

interface TransactionFormData extends RecurringFormFields {
  transaction_date: string;
  reference?: string;
  description: string;
  total_amount: number;
  project_id?: string;
  business_id?: string;
  fund_id?: string;
  entries: TransactionEntry[];
}

interface Transaction {
  id: string;
  transaction_date: string;
  reference?: string;
  description: string;
  total_amount: number;
  project_id?: string;
  business_id?: string;
  fund_id?: string;
  entries: TransactionEntry[];
}

interface CreateOrUpdateTransactionModalProps {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction?: Transaction | null; // If provided, we're editing
  /** Show the "Make this recurring" section (feature-flag gated inside). */
  allowRecurring?: boolean;
  /** Open with the recurring box already ticked (the "New recurring…" menu). */
  defaultRecurring?: boolean;
  /** Template-edit mode: hydrate from the template; saving PATCHes it in place. */
  recurringTemplate?: RecurringTemplateWire | null;
  /** Fires when the user ticks/unticks the box (this modal swaps its own footer label). */
  onRecurringChange?: (enabled: boolean) => void;
  /** After a recurring CREATE the modal navigates to `/accounting/recurring` unless `false`. */
  navigateOnSuccess?: boolean;
  /** Template-edit mode: the DangerZone's action — the host owns the confirm + delete. */
  onRequestDelete?: () => void;
}

export function CreateOrUpdateTransactionModal({
  organizationId,
  open,
  onOpenChange,
  transaction,
  allowRecurring = false,
  defaultRecurring = false,
  recurringTemplate = null,
  onRecurringChange,
  navigateOnSuccess,
  onRequestDelete,
}: CreateOrUpdateTransactionModalProps) {
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();
  const isEditing = !!transaction;
  const isTemplateMode = !!recurringTemplate;
  const { submit: submitRecurringTemplate, isPending: isRecurringPending } = useRecurringTemplateSubmit();

  // The ONE "today" the schedule uses — the org's calendar day (docs/DATES.md).
  const orgToday = useMemo(
    () => todayInOrgZone(selectedOrganization?.timezone),
    [selectedOrganization?.timezone],
  );

  // Template-edit mode: the stored payload is jsonb — re-validate before hydrating.
  const templatePayload = useMemo(() => {
    if (!recurringTemplate) return null;
    const parsed = recurringPayloadSchemaByType.journal.safeParse(recurringTemplate.payload);
    if (!parsed.success) {
      captureException(new Error('Recurring journal template payload failed validation'), {
        context: 'Journal recurring template hydrate',
        templateId: recurringTemplate.id,
        issues: parsed.error.issues.map((i) => i.path.join('.')).join(','),
      });
      return null;
    }
    return parsed.data;
  }, [recurringTemplate]);

  // No zod resolver here — the schedule is validated in onSubmit with this context.
  const recurrenceContext: RecurrenceFormSchemaContext = {
    orgToday,
    documentType: 'journal',
    originalStartDate: recurringTemplate?.start_date ?? null,
    startLocked: (recurringTemplate?.occurrences_generated ?? 0) > 0,
  };
  // NL WKR: existing classifications keyed by ledger_entries.id (edit mode) —
  // merged into getFormValues by ENTRY ID. Disabled outside gated NL orgs.
  const { data: wkrByLineId, isLoading: isLoadingWkr } = useNlWkrSourceCategories(
    organizationId,
    'journal',
    isEditing ? transaction?.id : undefined,
  );
  const [useHelper, setUseHelper] = useState(!isEditing && !isTemplateMode); // Default to helper for new, manual for edit / template edit
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [helperAnswers, setHelperAnswers] = useState<Record<string, string>>({});
  const [carbonDataByEntry, setCarbonDataByEntry] = useState<Record<string, CarbonData | null>>({});
  const [budgetCategoriesByEntry, setBudgetCategoriesByEntry] = useState<Record<string, string[]>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const saveBudgetCategoryEntries = useSaveBudgetCategoryEntries();
  const deleteBudgetCategoryEntries = useDeleteBudgetCategoryEntriesByLedgerEntries();
  const { data: existingBudgetTags } = useBudgetCategoryEntriesByTransaction(
    isEditing ? transaction?.id : undefined
  );

  const { data: accounts } = useChartOfAccounts(organizationId);
  const showAccountCurrency = useShowAccountCurrency(accounts);
  const { data: funds } = useFunds(organizationId);
  const { data: projects } = useProjects(organizationId);
  const { data: businesses } = useBusinesses(organizationId);
  const createTransactionMutation = useCreateLedgerTransaction();
  const updateTransactionMutation = useUpdateLedgerTransaction();
  const updateHeaderMutation = useUpdateLedgerTransactionHeader();
  const { t } = useTranslation();
  // Declared journal (any loaded line tagged to a filed VAT return): derived
  // HERE from the entries every host already composes
  // (useComposedEditTransaction / the register), so no host needs a new prop.
  // When set the date and the lines freeze, and the save goes header-only.
  const declaredSettlementId = declaredSettlementIdOf(transaction?.entries);
  const isDeclared = declaredSettlementId !== null;
  // Read-only lookup for card disabled state — no side-effect creation
  const { accountId: suspenseReadOnlyId, isLoading: isSuspenseLoading } = useSuspenseAccountBalance(selectedOrganization?.id);
  // Only create the suspense account when the user actually picks "Classify Later"
  const { data: suspenseCreatedId } = useSuspenseAccount(
    selectedScenario === 'classify_later' ? selectedOrganization?.id : undefined
  );
  const suspenseAccountId = suspenseCreatedId ?? suspenseReadOnlyId;
  // Opening Balance Equity (system account, absent from pickers by design):
  // fetched only when the reclassification scenario is picked — the scenario
  // prefills its leg by id, the suspense-helper pattern.
  const { data: obeAccountId, isLoading: isObeLoading } = useOpeningBalanceEquityAccount(
    selectedOrganization?.id,
    { enabled: selectedScenario === 'reclassify_obe' },
  );

  // Fetch existing carbon results when editing
  useEffect(() => {
    const fetchCarbonResults = async () => {
      if (isEditing && transaction?.id && transaction?.entries) {
        try {
          const { data: carbonResults, error } = await supabase
            .from('carbon_emission_results')
            .select('*')
            .eq('transaction_id', transaction.id);

          if (error) throw error;

          if (carbonResults && carbonResults.length > 0) {
            // Match carbon results to entries by account_id, keyed by field.id
            // (mirrors budget-tag hydration so carbon survives row reorder)
            const carbonMap: Record<string, CarbonData | null> = {};
            const currentFields = fieldsRef.current;

            transaction.entries.forEach((entry, entryIndex) => {
              const fieldId = currentFields[entryIndex]?.id;
              if (!fieldId) return;
              // Find carbon result that matches this entry's account
              const matchingResult = carbonResults.find(
                result => result.account_id === entry.account_id
              );

              if (matchingResult) {
                // `quantity` preserves what the user typed (e.g. 12 kg) so
                // the NumberInput can round-trip it. Pre-fix rows have
                // NULL `quantity`; fall back to `multiplier_value` which
                // stored the raw input for them.
                const rawQuantity = matchingResult.quantity ?? matchingResult.multiplier_value;
                carbonMap[fieldId] = {
                  table_id: matchingResult.emission_table_id,
                  dimension_values: matchingResult.dimension_values as Record<string, string>,
                  quantity: rawQuantity,
                  quantity_unit_code: matchingResult.quantity_unit_code ?? null,
                  multiplier_value: matchingResult.multiplier_value,
                  emission_factor_id: matchingResult.emission_factor_id,
                  emission_factor_value: matchingResult.emission_factor_value,
                  calculated_emissions: matchingResult.calculated_emissions,
                };
              }
            });

            setCarbonDataByEntry(carbonMap);
            // Auto-expand rows with carbon data so it's visible on edit
            setExpandedRows(prev => new Set([...prev, ...Object.keys(carbonMap)]));
          }
        } catch (error) {
          console.error('Failed to fetch carbon results:', error);
          captureException(error as Error, {
            context: 'Journal entry carbon results hydration (edit)',
            transactionId: transaction?.id,
          });
        }
      }
    };

    fetchCarbonResults();
  }, [isEditing, transaction?.id, transaction?.entries]);

  // Populate budget category tags when editing
  useEffect(() => {
    if (isEditing && existingBudgetTags?.length && transaction?.entries) {
      // Fetch ledger entry IDs for this transaction to map tags to entry indices
      const loadBudgetTags = async () => {
        const { data: ledgerEntries } = await supabase
          .from('ledger_entries')
          .select('id, account_id')
          .eq('transaction_id', transaction.id)
          .order('created_at')
          .order('id');

        if (!ledgerEntries) return;

        const tagMap: Record<string, string[]> = {};
        const currentFields = fieldsRef.current;
        ledgerEntries.forEach((le: { id: string; account_id: string }, idx: number) => {
          const fieldId = currentFields[idx]?.id;
          if (!fieldId) return;
          const tags = existingBudgetTags
            .filter((t) => t.ledger_entry_id === le.id)
            .map((t) => t.budget_category_id);
          if (tags.length > 0) {
            tagMap[fieldId] = tags;
          }
        });
        setBudgetCategoriesByEntry(tagMap);
        // Auto-expand rows with budget tags so they're visible on edit
        setExpandedRows(prev => new Set([...prev, ...Object.keys(tagMap)]));
      };
      loadBudgetTags();
    }
  }, [isEditing, existingBudgetTags, transaction?.id, transaction?.entries]);

  // Helper function to flatten hierarchical accounts while maintaining parent-child relationship
  const flattenHierarchy = (hierarchicalAccounts: HierarchicalAccount[]): ChartOfAccount[] => {
    const flattened: ChartOfAccount[] = [];

    // Sort accounts alphabetically at each level
    const sortedAccounts = [...hierarchicalAccounts].sort((a, b) =>
      a.account_name.localeCompare(b.account_name)
    );

    // Recursively add accounts and their children
    const addAccountAndChildren = (account: HierarchicalAccount) => {
      // Add the parent account
      const { children, ...accountWithoutChildren } = account;
      flattened.push(accountWithoutChildren as ChartOfAccount);

      // Sort and add children if they exist
      if (children && children.length > 0) {
        const sortedChildren = [...children].sort((a, b) =>
          a.account_name.localeCompare(b.account_name)
        );
        sortedChildren.forEach(child => addAccountAndChildren(child));
      }
    };

    sortedAccounts.forEach(account => addAccountAndChildren(account));
    return flattened;
  };

  // Sort accounts hierarchically for display
  const sortedAccounts = (() => {
    if (!accounts) return [];

    // First filter by active status
    const filtered = accounts.filter(account => account.is_active);

    // Build hierarchical structure
    const hierarchical = buildAccountHierarchy(filtered);

    // Flatten while maintaining parent-child ordering
    return flattenHierarchy(hierarchical);
  })();

  // Recurring defaults go through defaultValues / reset(values) — never a
  // mount-time setValue, which would flip isDirty (the close-confirm gate).
  const recurringDefaults: RecurringFormFields = resolveRecurringDefaults({
    documentType: 'journal',
    orgToday,
    template: recurringTemplate,
    defaultRecurring,
  });

  const defaultFormValues: TransactionFormData = {
    // `ledger_transactions.transaction_date` is a plain `date` column — a
    // calendar day, never an instant. Today's LOCAL day (never `.toISOString()`).
    transaction_date: format(new Date(), 'yyyy-MM-dd'),
    description: '',
    total_amount: 0,
    reference: '',
    project_id: '',
    business_id: '',
    fund_id: '',
    entries: [
      { account_id: '', fund_id: '', debit_amount: 0, credit_amount: 0 },
      { account_id: '', fund_id: '', debit_amount: 0, credit_amount: 0 }
    ],
    ...recurringDefaults,
  };

  const getFormValues = (): TransactionFormData => {
    if (isTemplateMode && recurringTemplate) {
      // Template-edit mode: the document half from the stored payload, the
      // (disabled) date from the schedule's start, the box ticked.
      return {
        ...defaultFormValues,
        ...(templatePayload ? hydrateJournalFromPayload(templatePayload) : {}),
        transaction_date: recurringTemplate.start_date,
        ...recurringDefaults,
      };
    }
    if (isEditing && transaction) {
      // `transaction_date` is a plain `date` column, so the server already
      // hands us the calendar day. Slice rather than round-tripping through a
      // JS Date — `new Date('2026-03-15')` is UTC midnight and formats as the
      // 14th for any UTC− browser. The slice also absorbs any legacy
      // timestamptz string still sitting in a cache from before the column
      // conversion.
      const formattedDate = transaction.transaction_date
        ? transaction.transaction_date.slice(0, 10)
        : format(new Date(), 'yyyy-MM-dd');

      return {
        transaction_date: formattedDate,
        description: transaction.description,
        total_amount: transaction.total_amount,
        reference: transaction.reference || '',
        project_id: transaction.project_id || '',
        business_id: transaction.business_id || '',
        fund_id: transaction.fund_id || '',
        entries: transaction.entries.length > 0 ? transaction.entries.map(entry => ({
          account_id: entry.account_id,
          // Match-header hydration. Stored data cannot distinguish "stamped by
          // inherit at submit" from "user explicitly picked the header's fund",
          // so a line whose fund EQUALS the header's hydrates as '' (inherit) —
          // this keeps header edits propagating to their lines. A differing
          // line fund is an explicit override; a stored NULL under a header
          // fund is an explicit "no fund" (hydrating it as '' would let the
          // submit coalesce restamp it on an unrelated edit). With no header
          // fund there is nothing to inherit, so legacy rows (header NULL)
          // hydrate as '' / their own fund.
          fund_id: entry.fund_id
            ? (entry.fund_id === transaction.fund_id ? '' : entry.fund_id)
            : (transaction.fund_id ? NO_FUND_SENTINEL : ''),
          debit_amount: entry.debit_amount || 0,
          credit_amount: entry.credit_amount || 0,
          description: entry.description || '',
          // NL WKR: hydrate by LEDGER ENTRY ID — entries have no recoverable
          // ordinal (the edit query orders by debit_amount), so positional
          // mapping would attach categories to the wrong rows.
          wkr_category: (entry.id && wkrByLineId?.[entry.id]) || ''
        })) : [
          { account_id: '', fund_id: '', debit_amount: 0, credit_amount: 0 },
          { account_id: '', fund_id: '', debit_amount: 0, credit_amount: 0 }
        ],
        // A posted journal is never recurring; the section is not rendered in edit mode.
        recurring_enabled: false,
        recurrence: undefined,
      };
    }
    return defaultFormValues;
  };

  const form = useForm<TransactionFormData>({
    defaultValues: getFormValues()
  });
  const { handleSubmit, reset, watch, control, setValue, setError } = form;
  // While ticked, the transaction date is set by the schedule (field disabled,
  // value kept; the payload strip drops it).
  const recurringEnabled = watch('recurring_enabled');

  // EDIT mode only: hold the form (render gate below) and this reset until
  // the WKR source query has hydrated, so the one reset that runs carries the
  // categories. Derived as a single flag so CREATE mode is untouched — the
  // flag is constantly false there and the gate's true→false settle can never
  // re-fire the reset and wipe typed input (test-writer-caught regression:
  // depending on isLoadingWkr directly re-reset create mode ~200ms after
  // open). wkrByLineId is deliberately NOT a dep — a later background refetch
  // must not wipe in-progress edits (no dirty guard here, pre-existing trait).
  const wkrHydrationPending = isEditing && isLoadingWkr;

  // Bumped by the reset below so the fund auto-expand seed (further down) runs
  // on the commit where useFieldArray has REGENERATED its field ids — `reset`
  // replaces them, so reading `fieldsRef` inside the reset effect itself would
  // key the expansion to ids that no longer exist.
  const [resetGeneration, setResetGeneration] = useState(0);

  // Update form when transaction prop changes
  React.useEffect(() => {
    if (open && !wkrHydrationPending) {
      const formValues = getFormValues();
      reset(formValues);
      setUseHelper(!isEditing && !isTemplateMode); // Reset helper state based on edit mode
      // Clear per-line side-channel state to prevent stale data from previous edits
      setCarbonDataByEntry({});
      setBudgetCategoriesByEntry({});
      setExpandedRows(new Set());
      setResetGeneration((n) => n + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transaction, isEditing, wkrHydrationPending]);

  const resetModalState = () => {
    setUseHelper(!isEditing && !isTemplateMode);
    setSelectedScenario(null);
    setHelperAnswers({});
    setCarbonDataByEntry({});
    setBudgetCategoriesByEntry({});
    setExpandedRows(new Set());
    reset(getFormValues());
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries'
  });
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const watchedEntries = watch('entries');
  const watchedHeaderFundId = watch('fund_id');

  const fundsById = useMemo(
    () => new Map((funds ?? []).map((fund) => [fund.id, fund])),
    [funds],
  );

  // Label for the "Inherit" option / the header fund it points at. Falls back to
  // the raw id if the fund hasn't loaded yet (or is no longer visible).
  const headerFundCode = watchedHeaderFundId
    ? fundsById.get(watchedHeaderFundId)?.fund_code ?? watchedHeaderFundId
    : '';
  const inheritOptionLabel = headerFundCode
    ? t('ledgerTransactions.fund.inherit', { fund: headerFundCode })
    : t('ledgerTransactions.fund.inheritNone');

  // EDIT mode: rows that carry a fund override start expanded, so an override
  // is visible without hunting through "More" (mirrors the carbon/budget
  // hydration auto-expand). Legacy journals — header fund NULL, funds set per
  // line — therefore open with every funded line expanded and chipped.
  useEffect(() => {
    if (!resetGeneration || !isEditing) return;
    const entryValues = form.getValues('entries') ?? [];
    const overrideFieldIds = fieldsRef.current
      .filter((_, index) => (entryValues[index]?.fund_id ?? '') !== '')
      .map((field) => field.id);
    if (overrideFieldIds.length > 0) {
      setExpandedRows((prev) => new Set([...prev, ...overrideFieldIds]));
    }
  }, [resetGeneration, isEditing, form]);

  // The OBE system account is excluded from the accounts list (and thus from
  // sortedAccounts), so a scenario-prefilled OBE leg would render as an empty
  // cell. Merge its row into the entry pickers ONLY while an entry references
  // it — it must not become generally pickable.
  const { data: obeAccountRow } = useAccountById(
    obeAccountId && watchedEntries?.some((e) => e.account_id === obeAccountId)
      ? selectedOrganization?.id
      : undefined,
    obeAccountId ?? '',
  );
  const entryPickerAccounts = useMemo(() => {
    if (!obeAccountRow) return sortedAccounts;
    if (!watchedEntries?.some((e) => e.account_id === obeAccountRow.id)) return sortedAccounts;
    return [...sortedAccounts, obeAccountRow];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sortedAccounts is rebuilt per render (IIFE); key on its inputs instead
  }, [obeAccountRow, watchedEntries, accounts]);
  const totalDebits = watchedEntries?.reduce((sum, entry) => sum + (entry.debit_amount || 0), 0) || 0;
  const totalCredits = watchedEntries?.reduce((sum, entry) => sum + (entry.credit_amount || 0), 0) || 0;
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

  const hasValidEntries = watchedEntries?.some(entry =>
    entry.account_id &&
    ((entry.debit_amount && entry.debit_amount > 0) || (entry.credit_amount && entry.credit_amount > 0))
  ) || false;

  const canSubmit = isBalanced && hasValidEntries;

  // Helper function to find accounts by category and type
  const findAccountByCategory = (categoryName: string, typeName?: string, subtypeName?: string) => {
    return sortedAccounts?.find(a => {
      const matchesCategory = a.category?.category_name?.toLowerCase() === categoryName.toLowerCase();
      const matchesType = !typeName || a.type?.type_name?.toLowerCase() === typeName.toLowerCase();
      const matchesSubtype = !subtypeName || a.subtype?.subtype_name?.toLowerCase() === subtypeName.toLowerCase();
      return matchesCategory && matchesType && matchesSubtype;
    });
  };

  const findAccountByName = (searchTerms: string[]) => {
    return sortedAccounts?.find(a =>
      searchTerms.some(term =>
        a.account_name?.toLowerCase().includes(term.toLowerCase())
      )
    );
  };

  // Transaction Helper Scenarios. Receiving a grant is not a journal helper:
  // a grant is a fund (Funds page) whose award raises the AR invoices the
  // receipt is matched against (docs/FUND_ACCOUNTING.md).
  const transactionScenarios = [
    {
      id: 'office_supplies',
      title: 'Bought Office Supplies',
      description: 'Purchased supplies for the office with cash/check',
      icon: '📝',
      questions: [
        { type: 'amount', label: 'Amount spent', placeholder: '150' },
        { type: 'description', label: 'What was purchased', placeholder: 'Office supplies from Staples' }
      ],
      generateEntries: (answers: Record<string, string>) => {
        // Find office supplies expense account
        const expenseAccount = findAccountByName(['office supplies', 'supplies']) ||
                              findAccountByCategory('expenses', 'administrative expenses') ||
                              findAccountByCategory('expenses');

        // Find cash account
        const cashAccount = findAccountByName(['cash', 'bank', 'checking', 'savings']) ||
                           findAccountByCategory('assets', 'current assets');

        return [
          {
            account_id: expenseAccount?.id || '',
            debit_amount: parseFloat(answers.amount) || 0,
            credit_amount: 0
          },
          {
            account_id: cashAccount?.id || '',
            debit_amount: 0,
            credit_amount: parseFloat(answers.amount) || 0
          }
        ];
      }
    },
    {
      id: 'pay',
      title: 'Pay a Bill or Expense',
      description: 'Pay for utilities, rent, services, or other expenses',
      icon: '📄',
      questions: [
        { type: 'expense_account', label: 'What type of expense?', placeholder: 'Select expense category' },
        { type: 'amount', label: 'Amount paid', placeholder: '500' },
        { type: 'payment_account', label: 'Paid from which account?', placeholder: 'Select payment source' },
        { type: 'description', label: 'Expense details', placeholder: 'Monthly rent payment' }
      ],
      generateEntries: (answers: Record<string, string>) => {
        return [
          {
            account_id: answers.expense_account || '',
            debit_amount: parseFloat(answers.amount) || 0,
            credit_amount: 0
          },
          {
            account_id: answers.payment_account || '',
            debit_amount: 0,
            credit_amount: parseFloat(answers.amount) || 0
          }
        ];
      }
    },
    {
      id: 'credit_card_payment',
      title: 'Paid Credit Card',
      description: 'Made a payment toward credit card balance',
      icon: '💳',
      questions: [
        { type: 'credit_card_account', label: 'Which credit card did you pay?', placeholder: 'Select credit card account' },
        { type: 'amount', label: 'Payment amount', placeholder: '500' },
        { type: 'payment_account', label: 'Paid from which account?', placeholder: 'Select payment source' },
        { type: 'description', label: 'Payment details', placeholder: 'Monthly credit card payment' }
      ],
      generateEntries: (answers: Record<string, string>) => {
        return [
          {
            account_id: answers.credit_card_account || '',
            debit_amount: parseFloat(answers.amount) || 0,
            credit_amount: 0
          },
          {
            account_id: answers.payment_account || '',
            debit_amount: 0,
            credit_amount: parseFloat(answers.amount) || 0
          }
        ];
      }
    },
    {
      id: 'recognize_deferred',
      title: 'Recognize Deferred Grant Revenue',
      description: 'Convert deferred grant to earned revenue (when grant period applies)',
      icon: '✅',
      questions: [
        { type: 'amount', label: 'Amount to recognize as earned', placeholder: '5000' },
        { type: 'description', label: 'Recognition details', placeholder: 'Q1 grant revenue recognition' },
        { type: 'fund', label: t('ledgerTransactions.fund.helperQuestion'), placeholder: t('ledgerTransactions.fund.selectPlaceholder') }
      ],
      generateEntries: (answers: Record<string, string>) => {
        // Find deferred revenue account
        const deferredRevenueAccount = findAccountByCategory('liabilities', 'deferred revenue') ||
                                      findAccountByName(['deferred', 'deferred revenue', 'deferred grant']);

        // Find grant revenue account
        const grantRevenueAccount = findAccountByCategory('revenue', 'operating revenue', 'grant revenue') ||
                                   findAccountByName(['grant', 'grant revenue']) ||
                                   findAccountByCategory('revenue');

        return [
          {
            account_id: deferredRevenueAccount?.id || '',
            debit_amount: parseFloat(answers.amount) || 0,
            credit_amount: 0
          },
          {
            account_id: grantRevenueAccount?.id || '',
            debit_amount: 0,
            credit_amount: parseFloat(answers.amount) || 0
          }
        ];
      }
    },
    {
      id: 'classify_later',
      title: t('suspense.helperTitle'),
      description: t('suspense.helperDescription'),
      icon: '🔄',
      disabled: isSuspenseLoading || !suspenseReadOnlyId,
      questions: [
        { type: 'amount', label: 'Transaction amount', placeholder: '100' },
        { type: 'payment_account', label: 'Paid from which account?', placeholder: 'Select payment source' },
        { type: 'description', label: 'Transaction details', placeholder: 'Payment to classify later' }
      ],
      generateEntries: (answers: Record<string, string>) => {
        return [
          {
            account_id: suspenseAccountId || '',
            debit_amount: parseFloat(answers.amount) || 0,
            credit_amount: 0
          },
          {
            account_id: answers.payment_account || '',
            debit_amount: 0,
            credit_amount: parseFloat(answers.amount) || 0
          }
        ];
      }
    },
    {
      id: 'reclassify_obe',
      title: t('openingBalanceEquity.helperTitle'),
      description: t('openingBalanceEquity.helperDescription'),
      icon: '⚖️',
      questions: [
        { type: 'amount', label: t('openingBalanceEquity.amountLabel'), placeholder: '5000' },
        { type: 'equity_account', label: t('openingBalanceEquity.targetLabel'), placeholder: t('openingBalanceEquity.targetPlaceholder') },
        { type: 'description', label: t('openingBalanceEquity.descriptionLabel'), placeholder: t('openingBalanceEquity.descriptionPlaceholder') }
      ],
      generateEntries: (answers: Record<string, string>) => {
        // OBE is credit-normal (opening balances credit it for asset openings):
        // moving it into retained earnings / a fund = debit OBE, credit target.
        // The OBE leg is prefilled by id — the account is a system account,
        // fetched via useOpeningBalanceEquityAccount (suspense-helper pattern).
        return [
          {
            account_id: obeAccountId || '',
            debit_amount: parseFloat(answers.amount) || 0,
            credit_amount: 0
          },
          {
            account_id: answers.equity_account || '',
            debit_amount: 0,
            credit_amount: parseFloat(answers.amount) || 0
          }
        ];
      }
    }
  ];

  const handleHelperSubmit = () => {
    const scenario = transactionScenarios.find(s => s.id === selectedScenario);
    if (!scenario) return;

    try {
      const entries = scenario.generateEntries(helperAnswers);

      setValue('description', helperAnswers.description || scenario.title);
      // Fund is an optional answer on the deferred-grant recognition scenario
      // only; it stamps the header, and the generated lines inherit it
      // (explicit '' below). Scenarios without a fund question must leave a
      // header fund the user already picked alone.
      if (scenario.questions.some(q => q.type === 'fund')) {
        setValue('fund_id', helperAnswers.fund || '');
      }

      const currentFieldCount = fields.length;
      for (let i = currentFieldCount - 1; i >= 0; i--) {
        remove(i);
      }

      entries.forEach((entry, index) => {
        append({ ...entry, fund_id: '' });
        setValue(`entries.${index}.account_id`, entry.account_id);
        setValue(`entries.${index}.fund_id`, '');
        setValue(`entries.${index}.debit_amount`, entry.debit_amount || 0);
        setValue(`entries.${index}.credit_amount`, entry.credit_amount || 0);
      });

      // Reset per-line side-channel state so generated rows start clean
      setCarbonDataByEntry({});
      setBudgetCategoriesByEntry({});
      setExpandedRows(new Set());

      setUseHelper(false);
      setHelperAnswers({});
      setSelectedScenario(null);
    } catch (error) {
      console.error('Error generating transaction:', error);
      toast.error('Could not generate the journal entry from this scenario.');
      captureException(error as Error, {
        context: 'Journal entry helper generate',
        scenario: selectedScenario ?? undefined,
      });
    }
  };

  const onSubmit = async (formData: TransactionFormData) => {
    // The recurring fields are form-local — never part of the journal body.
    const { recurring_enabled, recurrence, ...data } = formData;

    // No zod resolver on this form: the schedule validates here, writing its
    // issues inline (recurring.form.errors.* keys the section renders).
    if (!validateRecurrence(formData, recurrenceContext, setError)) return;

    const validEntries = data.entries.filter(entry =>
      entry.account_id && ((entry.debit_amount && entry.debit_amount > 0) || (entry.credit_amount && entry.credit_amount > 0))
    );

    const transactionData = {
      ...data,
      // Total is derived from the (balanced) entries, not entered by hand.
      // max(debits, credits) stays correct even if a no-amount row is filtered.
      total_amount: Math.max(
        validEntries.reduce((sum, e) => sum + (e.debit_amount || 0), 0),
        validEntries.reduce((sum, e) => sum + (e.credit_amount || 0), 0),
      ),
      // Strip the form-state sentinels the strict wire schema rejects: '' is
      // not a valid wkr_category (undefined keys drop out in JSON), and the
      // loaded entry id is not a wire field.
      entries: validEntries.map(({ id: _id, ...e }) => ({
        ...e,
        // Fund inheritance resolves HERE (the RPCs don't coalesce header→lines):
        // '' inherits the header fund, the sentinel is an explicit no-fund that
        // must NOT inherit, anything else is the line's own fund. The sentinel
        // never leaves this component.
        fund_id: e.fund_id === NO_FUND_SENTINEL ? '' : (e.fund_id || data.fund_id || ''),
        wkr_category: (e.wkr_category || undefined) as NlWkrCategory | undefined,
      }))
    };

    const saveBudgetCategoryTags = async (entries: { id: string; account_id: string }[]) => {
      if (!entries.length) return;

      // Map field.id → ledger_entry_id by account (consuming each id once),
      // mirroring saveCarbonEmissions. Robust to the update RPC re-inserting
      // entries out of form order (positional mapping would mis-tag rows).
      const currentFields = fieldsRef.current;
      const remaining = [...entries];
      const fieldIdToEntryId = new Map<string, string>();
      for (let i = 0; i < currentFields.length; i++) {
        const entry = watchedEntries[i];
        const isValid = entry?.account_id &&
          ((entry.debit_amount && entry.debit_amount > 0) || (entry.credit_amount && entry.credit_amount > 0));
        if (!isValid) continue;
        const matchIdx = remaining.findIndex(e => e.account_id === entry.account_id);
        if (matchIdx === -1) continue;
        fieldIdToEntryId.set(currentFields[i].id, remaining[matchIdx].id);
        remaining.splice(matchIdx, 1);
      }

      const tagEntries: { ledger_entry_id: string; budget_category_id: string }[] = [];
      for (const [fieldId, categoryIds] of Object.entries(budgetCategoriesByEntry)) {
        const entryId = fieldIdToEntryId.get(fieldId);
        if (!entryId || !categoryIds.length) continue;
        for (const catId of categoryIds) {
          tagEntries.push({ ledger_entry_id: entryId, budget_category_id: catId });
        }
      }

      if (tagEntries.length > 0) {
        // Awaited so a failed (possibly partial, chunked) save reaches the
        // caller's "saved, but budget tags could not be updated" warning.
        await saveBudgetCategoryEntries.mutateAsync({
          entries: tagEntries,
          organizationId,
        });
      }
    };

    const saveCarbonEmissions = async (transactionId: string) => {
      // Map each row's field.id → its entry (valid entries only), mirroring
      // saveBudgetCategoryTags so carbon attaches to the correct account
      // regardless of empty/filtered rows (prevents wrong-account/crash).
      const currentFields = fieldsRef.current;
      const fieldIdToEntry = new Map<string, TransactionEntry>();
      for (let i = 0; i < currentFields.length; i++) {
        const entry = watchedEntries[i];
        const isValid = entry?.account_id &&
          ((entry.debit_amount && entry.debit_amount > 0) || (entry.credit_amount && entry.credit_amount > 0));
        if (isValid) {
          fieldIdToEntry.set(currentFields[i].id, entry);
        }
      }

      // Build the full carbon row set for this transaction and persist through the
      // unified RPC (server owns the math; delete-by-key replaces prior rows).
      const rows = Object.entries(carbonDataByEntry)
        .filter(([fieldId, carbonData]) => carbonData !== null && fieldIdToEntry.has(fieldId))
        .map(([fieldId, carbonData]) =>
          carbonDataToRow(fieldIdToEntry.get(fieldId)!.account_id, carbonData!),
        );

      try {
        await persistTransactionCarbonResults(organizationId, { transactionId }, rows);
        // Refresh carbon aggregates (dashboards, summaries, hydration) — the
        // ledger mutations only invalidate ledger/account keys.
        // …and the Impact slim index, which LISTS these rows
        // (useSustainabilityActivityIndex's liveness contract). This must fire
        // HERE, not from the ledger mutation: on create there is no ledger-side
        // invalidation of this prefix at all, and on update the mutation's
        // onSuccess runs BEFORE this write lands, so its refetch would race
        // against the pre-write rows.
        invalidateCarbonCaches(queryClient, organizationId);
      } catch (carbonError) {
        console.error('Failed to save carbon emissions:', carbonError);
        toast.warning('Journal entry saved, but carbon emissions could not be saved.');
        captureException(carbonError as Error, {
          context: 'Journal entry carbon emissions save',
          transactionId,
        });
      }
    };

    // "Make this recurring": save a TEMPLATE, not a journal. The payload is
    // the exact create wire body minus `transaction_date`; the shared chain
    // creates (or PATCHes) it, runs it when due today, and navigates to the
    // list BEFORE the modal closes (replace supersedes the modal's history
    // entry). Carbon / budget tags are skipped — nothing was posted to attach
    // them to. Failures are toasted by the recurring mutations themselves.
    if (recurring_enabled && recurrence) {
      try {
        const payload = buildRecurringPayload('journal', buildJournalCreateBody(transactionData));
        await submitRecurringTemplate({
          organizationId,
          documentType: 'journal',
          recurrence,
          payload,
          templateId: recurringTemplate?.id,
          navigateOnSuccess,
        });
        setCarbonDataByEntry({});
        setBudgetCategoriesByEntry({});
        setExpandedRows(new Set());
        onOpenChange(false);
      } catch {
        // Toasted + captured by the recurring mutations (MutationCache.onError).
      }
      return;
    }

    // Edit-mode post-save: carbon + budget tags, then close. Shared by the
    // full save and the header-only save — neither block touches
    // `ledger_entries` (carbon keys by transaction, budget tags re-read the
    // entry ids), so both run unchanged on a declared journal whose lines
    // (and therefore entry ids) the header RPC leaves in place.
    const afterEditSaved = async (transactionId: string) => {
      // Persist carbon (the RPC deletes prior rows by transaction_id first).
      await saveCarbonEmissions(transactionId);

      // Delete old budget tags and save new ones. Match new entry IDs to
      // form rows by account (the update RPC re-inserts rows out of form
      // order); isolate failures so they can't abort the close.
      try {
        const { data: oldEntries } = await supabase
          .from('ledger_entries')
          .select('id')
          .eq('transaction_id', transactionId);
        if (oldEntries?.length) {
          await deleteBudgetCategoryEntries.mutateAsync({
            ledgerEntryIds: oldEntries.map((e: { id: string }) => e.id),
            organizationId,
          });
        }
        const { data: newEntries } = await supabase
          .from('ledger_entries')
          .select('id, account_id')
          .eq('transaction_id', transactionId);
        await saveBudgetCategoryTags(
          (newEntries ?? []).map((e: { id: string; account_id: string }) => ({ id: e.id, account_id: e.account_id }))
        );
      } catch (budgetError) {
        console.error('Failed to update budget tags:', budgetError);
        toast.warning('Journal entry saved, but budget tags could not be updated.');
        captureException(budgetError as Error, {
          context: 'Journal entry budget tags update (edit)',
          transactionId,
        });
      }

      setCarbonDataByEntry({});
      setBudgetCategoriesByEntry({});
      setExpandedRows(new Set());
      onOpenChange(false);
    };

    if (isEditing && transaction && isDeclared) {
      // Declared journal: header fields only — the lines are frozen on the
      // filed return and the full RPC would be refused by the DB guard.
      // `purchase_order_id` / `invoice_number` are not form fields here and
      // go through as null, exactly as on the full save. No local onError
      // toast: the hook already toasts the refusal text.
      updateHeaderMutation.mutate(
        {
          organizationId,
          transactionId: transaction.id,
          description: data.description,
          reference: data.reference,
          project_id: data.project_id,
          business_id: data.business_id,
          fund_id: data.fund_id,
        },
        { onSuccess: () => afterEditSaved(transaction.id) },
      );
    } else if (isEditing && transaction) {
      // Update existing transaction
      updateTransactionMutation.mutate(
        { id: transaction.id, organizationId, data: transactionData },
        {
          onSuccess: () => afterEditSaved(transaction.id),
          onError: () => {
            toast.errorTemplates.update('journal entry');
          },
        }
      );
    } else {
      // Create new transaction
      createTransactionMutation.mutate(
        { organizationId, data: transactionData },
        {
          onSuccess: async (result) => {
            if (result?.id) {
              await saveCarbonEmissions(result.id);
              // entry_ids come back in form (insertion) order; pair with the
              // valid entries to map budget tags by account.
              try {
                await saveBudgetCategoryTags(
                  (result.entry_ids ?? []).map((id: string, i: number) => ({
                    id,
                    account_id: validEntries[i]?.account_id ?? '',
                  })),
                );
              } catch (budgetError) {
                toast.warning('Journal entry saved, but budget tags could not be updated.');
                captureException(budgetError as Error, {
                  context: 'Journal entry budget tags save (create)',
                  transactionId: result.id,
                });
              }
            }
            setCarbonDataByEntry({});
            setBudgetCategoriesByEntry({});
            setExpandedRows(new Set());
            // No transaction detail route exists (transactions live in a list
            // with expandable rows), so we close in place rather than navigate.
            onOpenChange(false);
          },
          onError: () => {
            toast.errorTemplates.create('journal entry');
          },
        }
      );
    }
  };

  // What the schedule seeds from the moment the box is ticked — read live.
  const getRecurrenceSeed = () => {
    const values = form.getValues();
    return { documentDate: values.transaction_date, nameBase: values.description };
  };

  const addEntry = () => {
    // fund_id '' — a new line inherits the header fund until overridden.
    append({ account_id: '', fund_id: '', debit_amount: 0, credit_amount: 0 });
  };

  const toggleRow = (fieldId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) {
        next.delete(fieldId);
      } else {
        next.add(fieldId);
      }
      return next;
    });
  };

  return (
    <FullscreenFormDialog
      open={open}
      onOpenChange={onOpenChange}
      onClose={resetModalState}
      title={
        isTemplateMode
          ? t('recurring.dialogs.editTitle', { doc: t('recurring.words.journal', { count: 1 }) })
          : isEditing
            ? 'Edit Journal Entry'
            : 'Journal Entry'
      }
      onSubmit={handleSubmit(onSubmit)}
      formId="transaction-form"
      submitLabel={
        recurringEnabled
          ? t(isTemplateMode ? 'recurring.form.submit.update' : 'recurring.form.submit.create')
          : isEditing
            ? 'Update Journal Entry'
            : 'Create Journal Entry'
      }
      isSubmitting={
        createTransactionMutation.isPending ||
        updateTransactionMutation.isPending ||
        updateHeaderMutation.isPending ||
        isRecurringPending
      }
      submitDisabled={!canSubmit}
      skipCloseConfirmation={!form.formState.isDirty}
    >
      {wkrHydrationPending ? (
        // Hold the form body until WKR classifications hydrate — rendering it
        // earlier opens a window where user edits get discarded by the
        // readiness reset (and a pre-settle save would wipe the journal's
        // classifications). Non-gated orgs settle instantly.
        <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
          Loading…
        </div>
      ) : (
      <Form {...form}>
      <TooltipProvider>
        <div className="space-y-8">
          {/* Declared on a filed VAT return: says which inputs below are
              frozen and why. Renders nothing on an undeclared journal. */}
          <DeclaredVatBanner organizationId={organizationId} settlementId={declaredSettlementId} />

          {/* Helper — opt-in assistant panel above the document */}
          {useHelper && (
            <Card className="border-releve-teal-light/30 bg-releve-teal-pale/50">
              <CardContent className="p-4">
                {!selectedScenario ? (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="h-5 w-5 text-releve-teal" />
                      <h4 className="font-medium text-releve-teal-dark">Choose a transaction type:</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setUseHelper(false)}
                        className="ml-auto"
                      >
                        Manual Entry Instead
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
                      {transactionScenarios.map((scenario) => (
                        <Button
                          key={scenario.id}
                          type="button"
                          variant="outline"
                          className="min-h-24 h-auto p-4 flex flex-col items-start justify-start gap-2 hover:bg-white text-left hover:text-foreground"
                          onClick={() => setSelectedScenario(scenario.id)}
                          disabled={'disabled' in scenario && !!scenario.disabled}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <span className="text-xl flex-shrink-0">{scenario.icon}</span>
                            <span className="font-medium text-sm break-words text-foreground">{scenario.title}</span>
                          </div>
                          <span className="text-xs text-muted-foreground text-left break-words flex-grow hover:text-muted-foreground whitespace-normal">
                            {scenario.description}
                          </span>
                        </Button>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    {(() => {
                      const scenario = transactionScenarios.find(s => s.id === selectedScenario);
                      if (!scenario) return null;

                      return (
                        <>
                          <div className="flex items-center gap-2 mb-4">
                            <span className="text-xl">{scenario.icon}</span>
                            <h3 className="font-medium">{scenario.title}</h3>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedScenario(null);
                                setHelperAnswers({});
                              }}
                            >
                              ← Back
                            </Button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {scenario.questions.map((question, index) => (
                              <div key={index}>
                                <Label>{question.label}</Label>
                                {question.type === 'credit_card_account' ? (
                                  <AccountSelect
                                    accounts={sortedAccounts?.filter(a => a.category?.category_name?.toLowerCase() === 'liabilities') || []}
                                    showCurrency={showAccountCurrency}
                                    value={helperAnswers[question.type] || ''}
                                    onChange={(value) => setHelperAnswers(prev => ({
                                      ...prev,
                                      [question.type]: value
                                    }))}
                                    placeholder={question.placeholder}
                                    prioritizeCategory="Liabilities"
                                  />
                                ) : question.type === 'payment_account' ? (
                                  <AccountSelect
                                    accounts={sortedAccounts?.filter(a => a.category?.category_name?.toLowerCase() === 'assets') || []}
                                    showCurrency={showAccountCurrency}
                                    value={helperAnswers[question.type] || ''}
                                    onChange={(value) => setHelperAnswers(prev => ({
                                      ...prev,
                                      [question.type]: value
                                    }))}
                                    placeholder={question.placeholder}
                                    prioritizeCategory="Assets"
                                  />
                                ) : question.type === 'expense_account' ? (
                                  <AccountSelect
                                    accounts={sortedAccounts?.filter(a => a.category?.category_name?.toLowerCase() === 'expenses') || []}
                                    showCurrency={showAccountCurrency}
                                    value={helperAnswers[question.type] || ''}
                                    onChange={(value) => setHelperAnswers(prev => ({
                                      ...prev,
                                      [question.type]: value
                                    }))}
                                    placeholder={question.placeholder}
                                    prioritizeCategory="Expenses"
                                  />
                                ) : question.type === 'equity_account' ? (
                                  <AccountSelect
                                    accounts={sortedAccounts?.filter(a => a.category?.category_name?.toLowerCase() === 'equity') || []}
                                    showCurrency={showAccountCurrency}
                                    value={helperAnswers[question.type] || ''}
                                    onChange={(value) => setHelperAnswers(prev => ({
                                      ...prev,
                                      [question.type]: value
                                    }))}
                                    placeholder={question.placeholder}
                                    prioritizeCategory="Equity"
                                  />
                                ) : question.type === 'fund' ? (
                                  <FundSelect
                                    funds={funds ?? []}
                                    value={helperAnswers.fund || NO_FUND_OPTION}
                                    onValueChange={(value) => setHelperAnswers(prev => ({
                                      ...prev,
                                      fund: value === NO_FUND_OPTION ? '' : value
                                    }))}
                                    placeholder={question.placeholder}
                                    specialOptions={[
                                      { value: NO_FUND_OPTION, label: t('ledgerTransactions.fund.none') },
                                    ]}
                                    aria-label={question.label}
                                  />
                                ) : question.type === 'amount' ? (
                                  <NumberInput
                                    step={0.01}
                                    placeholder={question.placeholder}
                                    value={helperAnswers[question.type] || ''}
                                    onChange={(value) => setHelperAnswers(prev => ({
                                      ...prev,
                                      [question.type]: String(value)
                                    }))}
                                    allowEmpty
                                  />
                                ) : (
                                  <Input
                                    type="text"
                                    placeholder={question.placeholder}
                                    value={helperAnswers[question.type] || ''}
                                    onChange={(e) => setHelperAnswers(prev => ({
                                      ...prev,
                                      [question.type]: e.target.value
                                    }))}
                                  />
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="flex gap-2 pt-2">
                            <Button
                              type="button"
                              onClick={handleHelperSubmit}
                              disabled={
                                !helperAnswers.amount ||
                                !helperAnswers.description ||
                                (selectedScenario === 'credit_card_payment' && (!helperAnswers.credit_card_account || !helperAnswers.payment_account)) ||
                                (selectedScenario === 'pay' && (!helperAnswers.expense_account || !helperAnswers.payment_account)) ||
                                (selectedScenario === 'reclassify_obe' && (!helperAnswers.equity_account || isObeLoading || !obeAccountId))
                              }
                            >
                              Generate Transaction
                            </Button>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Metadata / links — internal associations above the document */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project (Optional)</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                    value={field.value || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No project</SelectItem>
                      {projects?.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="business_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business (Optional)</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                    value={field.value || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select business" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No business</SelectItem>
                      {businesses?.map((business) => (
                        <SelectItem key={business.id} value={business.id}>
                          {business.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            {/* Document-level fund — stamps every line at submit unless the line
                overrides it (the override lives under "More"). `formControl`
                wires the FormLabel to FundSelect's trigger.
                Frozen on a declared journal together with the per-entry funds
                (Clay's ruling): the header-only save never touches entry
                fund_id, and a header fund the entries don't follow would move
                nothing in the fund reports while showing every row as an
                override on the next open. */}
            <FormField
              control={form.control}
              name="fund_id"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-1">
                    <FormLabel>{t('ledgerTransactions.fund.headerLabel')}</FormLabel>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">{t('ledgerTransactions.fund.headerTooltip')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <FundSelect
                    funds={funds ?? []}
                    value={field.value || NO_FUND_OPTION}
                    onValueChange={(value) => field.onChange(value === NO_FUND_OPTION ? '' : value)}
                    placeholder={t('ledgerTransactions.fund.selectPlaceholder')}
                    specialOptions={[
                      { value: NO_FUND_OPTION, label: t('ledgerTransactions.fund.none') },
                    ]}
                    aria-label={t('ledgerTransactions.fund.headerLabel')}
                    disabled={isDeclared}
                    formControl
                  />
                </FormItem>
              )}
            />
          </div>

          {/* Document card */}
          <Card>
            <CardContent className="pt-6 space-y-8">
              <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                Journal Entry
              </h2>

              {/* Details band */}
              <div className="-mx-6 px-6 py-6 bg-muted space-y-5">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Details</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="transaction_date"
                        rules={{ required: true }}
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="font-extralight">Transaction Date</FormLabel>
                            <FormControl>
                              <DatePicker
                                value={parseLocalISODate(field.value)}
                                onChange={(date) => field.onChange(date ? format(date, 'yyyy-MM-dd') : '')}
                                // Set by the schedule on a recurring template; the
                                // date moves the VAT lines between periods, so it is
                                // frozen with them on a declared journal.
                                disabled={recurringEnabled || isDeclared}
                                className={fieldState.error ? "border-destructive" : ""}
                              />
                            </FormControl>
                            {recurringEnabled && (
                              // Theme-aware band (bg-muted) → default muted hint colour.
                              <p className="text-xs text-muted-foreground mt-1">{t('recurring.form.setBySchedule')}</p>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="reference"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-extralight">Reference (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g., Invoice #123" {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="description"
                      rules={{ required: 'Description is required' }}
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <div className="flex items-center gap-2">
                            <FormLabel className="font-extralight">Description</FormLabel>
                            {fieldState.error && (
                              <span className="text-xs text-destructive">{fieldState.error.message}</span>
                            )}
                          </div>
                          <FormControl>
                            <Textarea
                              placeholder="Describe the transaction..."
                              className={fieldState.error ? "border-destructive" : ""}
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Journal entry lines */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Journal Entries</h3>
                  {!useHelper && (
                    <Button
                      type="button"
                      onClick={() => setUseHelper(true)}
                      variant="outline"
                      size="sm"
                      // The helper regenerates the lines — locked with them.
                      disabled={isDeclared}
                    >
                      <Lightbulb className="h-4 w-4 mr-2" />
                      Use Helper
                    </Button>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <Table className="text-sm">
                    <TableHeader className="bg-releve-gray-light">
                      <TableRow className="border-b border-border/50">
                        <TableHead className="w-[280px]">
                          <div className="flex items-center gap-1">
                            <span>Account *</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs">
                                  <strong>Required:</strong> The specific account where this transaction is recorded.
                                  Determines which financial statement line item is affected (e.g., Cash, Expenses, Revenue).
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableHead>
                        <TableHead className="w-[120px]">
                          <div className="flex items-center gap-1">
                            <span>Debit</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <div className="space-y-1">
                                  <p className="font-medium">Use DEBIT when:</p>
                                  <p className="text-xs">• Increasing Assets (Cash, Equipment)</p>
                                  <p className="text-xs">• Increasing Expenses (Salaries, Rent)</p>
                                  <p className="text-xs">• Decreasing Liabilities or Revenue</p>
                                  <div className="border-t pt-1 mt-2">
                                    <p className="text-xs text-muted-foreground">
                                      <strong>Example:</strong> Received $1,000 grant →<br/>
                                      Debit: Cash $1,000
                                    </p>
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableHead>
                        <TableHead className="w-[120px]">
                          <div className="flex items-center gap-1">
                            <span>Credit</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <div className="space-y-1">
                                  <p className="font-medium">Use CREDIT when:</p>
                                  <p className="text-xs">• Increasing Liabilities (Credit Card, Loans)</p>
                                  <p className="text-xs">• Increasing Revenue (Grants, Donations)</p>
                                  <p className="text-xs">• Increasing Equity (Net Assets)</p>
                                  <p className="text-xs">• Decreasing Assets or Expenses</p>
                                  <div className="border-t pt-1 mt-2">
                                    <p className="text-xs text-muted-foreground">
                                      <strong>Example:</strong> Received $1,000 grant →<br/>
                                      Credit: Grant Revenue $1,000
                                    </p>
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableHead>
                        <TableHead className="w-[40px]"></TableHead>
                        <TableHead className="w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fields.map((field, index) => (
                        <React.Fragment key={field.id}>
                          <TableRow className="border-b border-border/30 align-top">
                            <TableCell className="p-1">
                              <AccountSelect
                                accounts={entryPickerAccounts || []}
                                showCurrency={showAccountCurrency}
                                value={watchedEntries?.[index]?.account_id || ''}
                                onChange={(value) => setValue(`entries.${index}.account_id`, value, { shouldDirty: true })}
                                placeholder="Select account *"
                                className={!watchedEntries?.[index]?.account_id ? 'border-destructive' : ''}
                                disabled={isDeclared}
                              />
                              {/* Fund override marker — only rows that DON'T
                                  inherit the header fund carry one. */}
                              {watchedEntries?.[index]?.fund_id ? (
                                <p className="mt-1 px-1 text-xs text-muted-foreground">
                                  {watchedEntries[index].fund_id === NO_FUND_SENTINEL
                                    ? t('ledgerTransactions.fund.chipNone')
                                    : t('ledgerTransactions.fund.chip', {
                                        code:
                                          fundsById.get(watchedEntries[index].fund_id!)?.fund_code ??
                                          watchedEntries[index].fund_id,
                                      })}
                                </p>
                              ) : null}
                            </TableCell>
                            <TableCell className="p-1">
                              <NumberInput
                                step={0.01}
                                value={watchedEntries?.[index]?.debit_amount ?? ''}
                                onChange={(value) => setValue(`entries.${index}.debit_amount`, value === '' ? 0 : Number(value), { shouldDirty: true })}
                                placeholder="0.00"
                                disabled={isDeclared}
                              />
                            </TableCell>
                            <TableCell className="p-1">
                              <NumberInput
                                step={0.01}
                                value={watchedEntries?.[index]?.credit_amount ?? ''}
                                onChange={(value) => setValue(`entries.${index}.credit_amount`, value === '' ? 0 : Number(value), { shouldDirty: true })}
                                placeholder="0.00"
                                disabled={isDeclared}
                              />
                            </TableCell>
                            <TableCell className="p-1 text-right">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 gap-1 px-2 text-xs text-muted-foreground"
                                onClick={() => toggleRow(field.id)}
                                aria-label="Toggle carbon & budget"
                                aria-expanded={expandedRows.has(field.id)}
                              >
                                {t('more')}
                                {expandedRows.has(field.id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </Button>
                            </TableCell>
                            <TableCell className="p-1">
                              {fields.length > 2 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  // Disabled, not hidden, on a declared journal
                                  // (forms disable, panels hide).
                                  disabled={isDeclared}
                                  aria-label="Remove entry"
                                  onClick={() => {
                                    remove(index);
                                    setExpandedRows((prev) => {
                                      const next = new Set(prev);
                                      next.delete(field.id);
                                      return next;
                                    });
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                          {expandedRows.has(field.id) && (
                            <TableRow>
                              <TableCell colSpan={5} className="bg-muted/30 p-3 space-y-2">
                                {/* Fund override — '' inherits the header fund;
                                    the no-fund sentinel opts this line out of
                                    the header stamp entirely. Controlled, so a
                                    saved line fund is visible on edit. */}
                                <div className="max-w-xs">
                                  <Label className="text-xs text-muted-foreground">
                                    {t('ledgerTransactions.fund.overrideLabel')}
                                  </Label>
                                  <FundSelect
                                    funds={funds ?? []}
                                    value={watchedEntries?.[index]?.fund_id || INHERIT_FUND_VALUE}
                                    onValueChange={(value) =>
                                      setValue(
                                        `entries.${index}.fund_id`,
                                        value === INHERIT_FUND_VALUE ? '' : value,
                                        { shouldDirty: true },
                                      )
                                    }
                                    placeholder={t('ledgerTransactions.fund.selectPlaceholder')}
                                    specialOptions={[
                                      { value: INHERIT_FUND_VALUE, label: inheritOptionLabel },
                                      { value: NO_FUND_SENTINEL, label: t('ledgerTransactions.fund.none') },
                                    ]}
                                    aria-label={t('ledgerTransactions.fund.overrideLabel')}
                                    // The header-only save never rewrites a
                                    // line's fund; the header fund stays live.
                                    disabled={isDeclared}
                                  />
                                </div>
                                {watchedEntries?.[index]?.account_id && (
                                  <LineItemCarbon
                                    accountId={watchedEntries[index].account_id}
                                    value={carbonDataByEntry[field.id]}
                                    onChange={(data) => {
                                      setCarbonDataByEntry(prev => ({
                                        ...prev,
                                        [field.id]: data
                                      }));
                                    }}
                                  />
                                )}
                                <BudgetCategoryPicker
                                  selectedCategoryIds={budgetCategoriesByEntry[field.id] ?? []}
                                  onChange={(categoryIds) =>
                                    setBudgetCategoriesByEntry((prev) => ({
                                      ...prev,
                                      [field.id]: categoryIds,
                                    }))
                                  }
                                />
                                {/* NL WKR classifier — form-bound (rides the entries
                                    payload), self-gates on flag+jurisdiction. */}
                                <div className="max-w-xs">
                                  <WkrCategorySelect
                                    value={(watchedEntries?.[index]?.wkr_category as NlWkrCategory | undefined) || null}
                                    onChange={(category) =>
                                      setValue(`entries.${index}.wkr_category`, category ?? '', { shouldDirty: true })
                                    }
                                    disabled={isDeclared}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-3 border-dashed border-2 text-muted-foreground hover:text-accent-foreground hover:border-solid"
                  onClick={addEntry}
                  disabled={isDeclared}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Entry
                </Button>

                {/* Balance summary */}
                <div className="bg-muted p-4 rounded-lg mt-4">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Total Debits:</span> <CurrencyDisplay value={totalDebits} />
                    </div>
                    <div>
                      <span className="font-medium">Total Credits:</span> <CurrencyDisplay value={totalCredits} />
                    </div>
                    <div className={`flex items-center gap-1 font-medium ${isBalanced ? 'text-success' : 'text-destructive'}`}>
                      {isBalanced ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                      {isBalanced ? 'Balanced' : 'Out of Balance'}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SECTION: MAKE THIS RECURRING — always the last block (only the
              template-edit DangerZone follows). Never when editing a posted journal. */}
          {(allowRecurring || isTemplateMode) && !isEditing && (
            <RecurringSection
              documentType="journal"
              orgToday={orgToday}
              getSeed={getRecurrenceSeed}
              template={recurringTemplate}
              onEnabledChange={onRecurringChange}
            />
          )}

          {isTemplateMode && onRequestDelete && (
            <DangerZone
              title={t('recurring.dangerZone.heading')}
              actionTitle={t('recurring.dangerZone.title')}
              description={t('recurring.dangerZone.description')}
              buttonLabel={t('recurring.dangerZone.action')}
              onAction={onRequestDelete}
            />
          )}
        </div>
      </TooltipProvider>
      </Form>
      )}
    </FullscreenFormDialog>
  );
}
